import {
  AdgroupConfig,
  AdgroupFormData,
  AudienceEstimate,
  BaseMinDailyBudget,
  BudgetMode,
  BudgetOptimizeSwitch,
  BudgetTypeEnum,
  budgetTypeEnumLite,
  CampaignFormData,
  CreateMode,
  SplitTestFromData,
} from '@tt4b/ttam-enums';
import {
  useRequest,
  useRecommendBudgetInfoApi,
  useRecommendBudgetReturnApi,
  useStdAdgroupIs,
  useStdObjectiveType,
  useStdAccountStore,
  ValidationError,
  useStore,
  StateEnum,
  useStdAdgroupCbo,
} from '@tt4b/ttam-hooks';
import { localizeCurrency, computedDiffDays, LocationParams } from '@tt4b/ttam-utils';

import { computed, Ref, ref, ComputedRef } from '@vue/composition-api';
import { useCurrencyOption } from './../../../hooks/creation/account/useCurrencyOption/index';
import { i18n } from './../../../i18n';
import { BudgetRecommendResponse, BudgetSourceType } from './types';
import { isNumber } from 'lodash-es';

/** Common  */
export function useCommonMinBudget(minimumBudgetBase?: Ref<number>) {
  const { currencyOption } = useCurrencyOption();
  return {
    minBudget: computed(() => {
      if (isNumber(minimumBudgetBase?.value)) {
        return minimumBudgetBase?.value;
      }
      return BaseMinDailyBudget * currencyOption.value.radio;
    }),
  };
}

export function useCurrencyRatio(value?: number) {
  const { currencyOption } = useCurrencyOption();

  return value * currencyOption.value.radio;
}

export function useCommonIsBudgetRecommendVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  createMode: Ref<CreateMode>;
}) {
  const { adgroupData, campaignData, createMode } = props;
  const { isConversion } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const { isSelfAccount } = useStdAccountStore();

  const { isWebsite, isDailyBudget, isHasTikTok, isHasTopbuzz } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const isBudgetRecommendVisible = computed(
    () =>
      createMode.value === CreateMode.CREATE &&
      isSelfAccount.value &&
      isConversion.value &&
      isWebsite.value &&
      isDailyBudget.value &&
      (isHasTikTok.value || isHasTopbuzz.value),
  );

  return isBudgetRecommendVisible;
}

/** ScheduleBudget  */
export function useScheduledBudgetPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
}) {
  const { adgroupData, campaignData, adgroupConfig } = props;
  const { isCBO } = useStdAdgroupCbo({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  return {
    visible: computed(
      () =>
        adgroupConfig.value?.showScheduledBudget &&
        [BudgetMode.DAILY_BUDGET, BudgetMode.DYNAMIC_DAILY_BUDGET].includes(adgroupData.value.budgetMode) &&
        !isCBO.value,
    ),
  };
}

/** Set  */
export function useSetPropsBudgetModeOptions(props: {
  createMode: Ref<CreateMode>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  lite: Ref<boolean>;
}) {
  const { createMode, adgroupData, campaignData, lite } = props;

  const { isSupportDynamicDailyBudget } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  const isDailyMode = computed(
    () =>
      [BudgetMode.DAILY_BUDGET, BudgetMode.DYNAMIC_DAILY_BUDGET].includes(campaignData.value.budgetMode) &&
      Number(campaignData.value.budget) > 0,
  );

  if (lite.value) {
    return {
      budgetModeOptions: computed(() =>
        budgetTypeEnumLite().map(o => ({
          ...o,
          hide: isDailyMode.value && o.value === BudgetMode.TOTAL_BUDGET,
        })),
      ),
    };
  }
  return {
    budgetModeOptions: computed(() => {
      const { budgetMode } = adgroupData.value;
      const { DAILY_BUDGET, DYNAMIC_DAILY_BUDGET } = BudgetMode;
      let needFilterMode: BudgetMode;
      if (createMode.value === CreateMode.EDIT) {
        needFilterMode = budgetMode === DAILY_BUDGET ? DYNAMIC_DAILY_BUDGET : DAILY_BUDGET;
      } else {
        needFilterMode = isSupportDynamicDailyBudget.value ? BudgetMode.DAILY_BUDGET : BudgetMode.DYNAMIC_DAILY_BUDGET;
      }

      return BudgetTypeEnum()
        .filter(i => i.value !== needFilterMode)
        .map(o => ({
          ...o,
          hide: isDailyMode.value && o.value === BudgetMode.TOTAL_BUDGET,
        }));
    }),
  };
}

export function useSetPropsBudgetModeDisabled(props: { adgroupConfig: Ref<AdgroupConfig> }) {
  return {
    budgetModeDisabled: computed(() => props?.adgroupConfig?.value?.disableBudgetType),
  };
}

export function useSetPropsPlaceholder(props: { adgroupData: Ref<AdgroupFormData>; minBudgetCalculated: Ref<number> }) {
  const { adgroupData, minBudgetCalculated } = props;
  const { minBudget } = useCommonMinBudget(minBudgetCalculated);
  return {
    placeholder: computed(() =>
      i18n.t('campaign_budget_placeholder', {
        min: [BudgetMode.DAILY_BUDGET, BudgetMode.DYNAMIC_DAILY_BUDGET].includes(adgroupData.value?.budgetMode)
          ? minBudget.value
          : minBudget.value * (computedDiffDays(adgroupData.value?.startTime, adgroupData.value?.endTime) || 1),
      }),
    ),
  };
}

export function useSplitTestPropsText({ adgroupData }: { adgroupData: Ref<AdgroupFormData> }) {
  const budgetModeTips = computed(() =>
    i18n.t(
      adgroupData.value?.budgetMode === BudgetMode.DYNAMIC_DAILY_BUDGET
        ? 'module_split_test_dynamic_daily_budget_tip'
        : 'module_split_test_budget_hint',
    ),
  );

  const budgetModeLabel = computed(() => {
    const bugetOption = BudgetTypeEnum().find(o => (o.value = adgroupData?.value?.budgetMode));
    return bugetOption?.label || '';
  });

  return {
    budgetModeLabel,
    budgetModeTips,
  };
}

export function useCommonPropsCurrencyOption() {
  return {
    currencyOption: useCurrencyOption()?.currencyOption,
  };
}

/** CboInfo  */
export function useCboInfoPropsText(props: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { campaignData } = props;
  const { currencyOption } = useCurrencyOption();

  const budgetCBOFormated = computed(() => {
    const budget = campaignData?.value?.budget || 0;
    return localizeCurrency(
      budget,
      currencyOption.value?.currency,
      currencyOption.value?.langCode,
      currencyOption.value?.country,
      currencyOption.value?.precision,
    );
  });
  const budgetCBOModeLabel = computed(() => {
    const bugetOption = BudgetTypeEnum().find(o => o.value === campaignData?.value?.budgetMode);

    return bugetOption?.label || '';
  });

  return {
    text: `${budgetCBOModeLabel.value}: ${currencyOption.value?.currency} ${budgetCBOFormated.value}`,
  };
}

export function useCboInfoPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { isCBO } = useStdAdgroupCbo({
    campaignFormData: props.campaignData,
    adgroupFormData: props.adgroupData,
  });
  return {
    visible: isCBO,
  };
}

/** LearningPhaseTips  */
export function useLearningPhaseTipsPropsVisible(props: {
  adgroupConfig: Ref<AdgroupConfig>;
  isLearningPhase: Ref<boolean>;
}) {
  const { adgroupConfig, isLearningPhase } = props;
  return {
    visible: computed(() => adgroupConfig?.value?.showLearningPhaseHint && isLearningPhase.value),
  };
}

/** Recommend  */
export function useRecommendPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  budgetRecommendData: Ref<BudgetRecommendResponse>;
  createMode: Ref<CreateMode>;
  minBudgetCalculated?: Ref<number>;
}) {
  const { campaignData, adgroupData, budgetRecommendData, createMode, minBudgetCalculated } = props;

  const { minBudget } = useCommonMinBudget(minBudgetCalculated);

  const isBudgetRecommendVisible = useCommonIsBudgetRecommendVisible({
    adgroupData,
    campaignData,
    createMode,
  });

  return {
    visible: computed(
      () => budgetRecommendData.value?.enoughBudget > minBudget.value && isBudgetRecommendVisible.value,
    ),
  };
}

export function useRecommendPropsOptions(props: { budgetRecommendData: Ref<BudgetRecommendResponse> }) {
  const { budgetRecommendData } = props;
  return {
    options: computed(() => {
      const options = [];
      if (!budgetRecommendData.value) {
        return options;
      }

      options.push({
        title: i18n.t('module_adgroup_budget_recommend_enough_title'),
        budget: budgetRecommendData.value.enoughBudget,
        impression: budgetRecommendData.value.enoughImpression,
        teaClick: 'budget_recommend_enough_click',
      });

      if (budgetRecommendData.value.competitiveBudgetAvailable) {
        options.push({
          title: i18n.t('module_adgroup_budget_recommend_competitive_title'),
          budget: budgetRecommendData.value.competitiveBudget,
          impression: budgetRecommendData.value.competitiveImpression,
          teaClick: 'budget_recommend_competitive_click',
        });
      }

      return options;
    }),
  };
}

/** 记录是否请求过预算推荐 */
let isRequestBudgetRecommend = false;
const budgetRecommendData = ref<BudgetRecommendResponse | undefined>();
export function useRecommendDataSource(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  audienceEstimate: Ref<AudienceEstimate>;
  createMode: Ref<CreateMode>;
}) {
  const { audienceEstimate, adgroupData, campaignData, createMode } = props;
  const { state } = useStore();
  const areaData = state[StateEnum.areaData].data;
  const areaMap = computed(() => areaData?.value?.areaMap ?? {});
  const estimateParam = computed(() => ({
    score: audienceEstimate.value?.score,
    lowerUserCount: audienceEstimate.value?.lowerUserCount,
    upperUserCount: audienceEstimate.value?.upperUserCount,
  }));

  const locationParams: ComputedRef<LocationParams> = computed(() => ({
    areaMap: areaMap.value,
    // 待 splittest支持
    isSplitTestBidding: false,
    isSplitTestCreative: false,
  }));

  const isBudgetRecommendVisible = useCommonIsBudgetRecommendVisible({
    adgroupData,
    campaignData,
    createMode,
  });

  const {
    data,
    isLoading: loading,
    run: getData,
    error,
  } = useRequest(
    useRecommendBudgetInfoApi,
    {
      adgroupForm: adgroupData.value,
      campaignForm: campaignData.value,
      estimateData: estimateParam.value,
      locationParams: locationParams.value,
    },
    { manual: true },
  );

  return {
    data: budgetRecommendData,
    loading,
    run: async (opt?: { onSuccess: () => void }) => {
      if (!isBudgetRecommendVisible.value) {
        budgetRecommendData.value = null;
        return;
      }
      isRequestBudgetRecommend = true;
      await getData();
      opt?.onSuccess?.();
      budgetRecommendData.value = error.value ? null : data.value;
    },
    postBudgetSourceEvent: async (params: { adId: string; campaignId: string; budgetSourceType: BudgetSourceType }) => {
      const { budgetSourceType, adId, campaignId } = params || {};
      if (!budgetSourceType || !isRequestBudgetRecommend) {
        return;
      }
      try {
        await useRecommendBudgetReturnApi({
          adgroupForm: adgroupData.value,
          campaignForm: campaignData.value,
          locationParams: locationParams.value,
          otherInfo: {
            ...estimateParam.value,
            ...budgetRecommendData.value,
            adId,
            campaignId,
            budgetSourceType,
          },
        });
      } catch (_) {}
    },
  };
}

export function useRules(props: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { adgroupData, campaignData } = props;
  return {
    budgetValidator: computed(() => [
      {
        trigger: 'custom',
        validator: (rule, value, callback, formData: AdgroupFormData | SplitTestFromData) => {
          // TODO 待splittest支持
          // const { isInSplitTestLevel, splitTestNum } = useSplitTestTool();
          const isInSplitTestLevel = ref(false);
          let adgroupFormData, campaignFormData, splitTestFormData;
          if (isInSplitTestLevel.value) {
            // const { campaignData, adgroupData, formData: sFormData } = useSplitTestStore();
            // campaignFormData = campaignData;
            // adgroupFormData = adgroupData;
            // splitTestFormData = sFormData;
          } else {
            adgroupFormData = adgroupData.value;
            campaignFormData = campaignData.value;
          }
          const { budgetOptimizeSwitch } = campaignFormData;
          if (budgetOptimizeSwitch === BudgetOptimizeSwitch.ACTIVATED) {
            return callback();
          }

          const budgetNum = Number(value);
          /**
           * 值正确
           */
          if (isNaN(budgetNum) || !budgetNum) {
            return callback(
              new ValidationError({
                messageI18nKey: 'adgroup_budget_hint',
                message: i18n.t('adgroup_budget_hint'),
                moduleName: i18n.t('adgroup_budgets_card'),
                subModuleName: i18n.t('adgroup_budget'),
              }),
            );
          }

          /**
           * 精度
           */
          const { currencyOption } = useCurrencyOption();

          const decimalLength = String(value).split('.')[1]?.length ?? 0;

          if (decimalLength !== currencyOption.value.precision) {
            return callback(
              new ValidationError({
                messageI18nKey: 'currency_decimal_valid',
                message: i18n.t('currency_decimal_valid', {
                  currency: i18n.t(`currency_${currencyOption.value.precision}`),
                }),
                moduleName: i18n.t('adgroup_budgets_card'),
                subModuleName: i18n.t('adgroup_budget'),
              }),
            );
          }

          /**
           * 最小值
           */
          const { minBudget } = useCommonMinBudget();
          let minBudgetVal = minBudget.value;
          // eslint-disable-next-line prefer-const
          let { budgetMode, startTime, endTime } = adgroupFormData;
          // TODO 待splittest支持
          if (isInSplitTestLevel.value) {
            // startTime = splitTestFormData.startTime;
            // endTime = splitTestFormData.endTime;
            // split test把预算平均分配到测试组和控制组
            // minBudget = minBudget * splitTestNum.value;
          }

          if (budgetMode === BudgetMode.TOTAL_BUDGET) {
            minBudgetVal = minBudgetVal * computedDiffDays(startTime, endTime);
          }
          if (budgetNum < minBudgetVal) {
            return callback(
              new ValidationError({
                messageI18nKey: 'campaign_budget_max_hint',
                message: i18n.t('campaign_budget_max_hint', { min: minBudgetVal }),
                moduleName: i18n.t('adgroup_budgets_card'),
                subModuleName: i18n.t('adgroup_budget'),
              }),
            );
          }

          return callback();
        },
      },
    ]),
  };
}
